import { produce } from "immer";
import { atom } from "jotai";

import { Logger } from "@moment/logging";

import { getCache } from "../_interop/cache";
import { env } from "../env/env";
import { Flag, defaultFlags } from "./feature-flags.types";

const logger = new Logger("feature-flags");

const $draftFeatureFlags = atom(null as Record<Flag, boolean> | null);
// Flags that default to true in local and staging environments
const devDefaultOverrides: Partial<Record<Flag, boolean>> =
	env.appEnv === "local" || env.appEnv === "staging"
		? {
				[Flag.EnableDocumentAttachments]: true,
				[Flag.Workflows]: true,
				[Flag.WorkspaceSecrets]: true,
			}
		: {};

const $cachedFeatureFlags = atom(async () => {
	const cache = getCache();

	const flags = { ...structuredClone(defaultFlags), ...devDefaultOverrides };

	try {
		const cachedFlags = await cache.featureFlags.getAll();

		Object.assign(flags, cachedFlags);
	} catch (error) {
		logger.error("Failed to get cached feature flags", { cause: error });
	}

	return flags;
});

export const $featureFlags = atom(
	async (get) => get($draftFeatureFlags) ?? (await get($cachedFeatureFlags)),
	async (get, set, update: [Flag, boolean]) => {
		const cache = getCache();

		// This has to be awaited because we're basically memo'ing promises
		const currentFlags = await get($featureFlags);

		// Optimistic update
		const [flagId, enabled] = update;
		const newFlags = produce(currentFlags, (draft) => {
			draft[flagId] = enabled;
		});

		set($draftFeatureFlags, newFlags);

		await cache.featureFlags.upsert(flagId, enabled);
	}
);
$featureFlags.debugLabel = "featureFlags";
